import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "styled-components";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import MetaTags from "react-meta-tags";
import GlobalStyles from "./styles";
import { getAuthData } from "./utils";
import { KYC_STATUS } from "./constants";
import Home from "./screens/Home/Home";
import Register from "./screens/Authentication/Register";
import Login from "./screens/Authentication/Login";
import ForgotPassword from "./screens/Authentication/ForgotPassword";
import KYCPending from "./screens/Authentication/KYCPending";
import PrivacyPolicy from "./screens/PublicPages/PrivacyPolicy";
import Disclaimers from "./screens/PublicPages/Disclaimers";
import TermsAndConditions from "./screens/PublicPages/TermsAndConditions";
import RefundPolicy from "./screens/PublicPages/RefundPolicy";
import Contact from "./screens/PublicPages/Contact";
import Profile from "./screens/Account/Profile";
import ChangePassword from "./screens/Account/ChangePassword";
import Logs from "./screens/Account/Logs";
import Sessions from "./screens/Account/Sessions";
import Dashboard from "./screens/Dashboard/Dashboard";
import WalletTopupsList from "./screens/Payments/WalletTopupsList";
import WalletTopupsCreate from "./screens/Payments/WalletTopupsCreate";
import BankAccountsList from "./screens/Payments/BankAccountsList";
import BankAccountsCreate from "./screens/Payments/BankAccountsCreate";
import WithdrawFundsList from "./screens/Payments/WithdrawFundsList";
import WithdrawFundsCreate from "./screens/Payments/WithdrawFundsCreate";
import SwitchFundsList from "./screens/Payments/SwitchFundsList";
import SwitchFundsCreate from "./screens/Payments/SwitchFundsCreate";
import FundTransfersList from "./screens/Payments/FundTransfersList";
import PaymentGatewayDeposits from "./screens/Payments/PaymentGatewayDeposits";
import PaymentGatewayDepositsStatus from "./screens/Payments/PaymentGatewayDepositsStatus";
import AvailableMarginsList from "./screens/Buyer/AvailableMarginsList";
import BuyerMarginsList from "./screens/Buyer/BuyerMarginsList";
import BuyerMarginsCreate from "./screens/Buyer/BuyerMarginsCreate";
import BuyerMarginsUpdate from "./screens/Buyer/BuyerMarginsUpdate";
import BuyerTransactionsList from "./screens/Buyer/BuyerTransactionsList";
import BuyerTransactionsPendingList from "./screens/Buyer/BuyerTransactionsPendingList";
import BuyerTransactionsComplaintsList from "./screens/Buyer/BuyerTransactionsComplaintsList";
import BuyerSearchTransactionsList from "./screens/Buyer/BuyerSearchTransactionsList";
import BuyerWrongToRightTransactionsList from "./screens/Buyer/BuyerWrongToRightTransactionsList";
// import BuyerAPICredentials from "./screens/Buyer/BuyerAPICredentials";
import BuyerAPISettingsList from "./screens/Buyer/BuyerAPISettingsList";
import BuyerAPISettingsCreate from "./screens/Buyer/BuyerAPISettingsCreate";
import BuyerAPISettingsUpdate from "./screens/Buyer/BuyerAPISettingsUpdate";
import BuyerAPIDocumentation from "./screens/Buyer/BuyerAPIDocumentation";
import SellerAPISettingsList from "./screens/Seller/SellerAPISettingsList";
import SellerAPISettingsCreate from "./screens/Seller/SellerAPISettingsCreate";
import SellerAPISettingsUpdate from "./screens/Seller/SellerAPISettingsUpdate";
import SellerMarginsList from "./screens/Seller/SellerMarginsList";
import SellerMarginsCreate from "./screens/Seller/SellerMarginsCreate";
import SellerMarginsUpdate from "./screens/Seller/SellerMarginsUpdate";
import SellerTransactionsList from "./screens/Seller/SellerTransactionsList";
import SellerTransactionsPendingList from "./screens/Seller/SellerTransactionsPendingList";
import SellerTransactionsComplaintsList from "./screens/Seller/SellerTransactionsComplaintsList";
import SellerSearchTransactionsList from "./screens/Seller/SellerSearchTransactionsList";
import SellerWrongToRightTransactionsList from "./screens/Seller/SellerWrongToRightTransactionsList";
import FakeComplaints from "./screens/Seller/FakeComplaints";
import OperatorDaybook from "./screens/Reports/OperatorDaybook";
import Daybook from "./screens/Reports/Daybook";
import BuyerWalletTransactions from "./screens/Reports/BuyerWalletTransactions";
import SellerWalletTransactions from "./screens/Reports/SellerWalletTransactions";
import BuyerInvoicesList from "./screens/Invoicing/BuyerInvoicesList";
import SellerInvoicesList from "./screens/Invoicing/SellerInvoicesList";
import Support from "./screens/Help/Support";
import SupportCreate from "./screens/Help/SupportCreate";
import SellerAPISupport from "./screens/Help/SellerAPISupport";
import SellerAPISupportCreate from "./screens/Help/SellerAPISupportCreate";
import FirmDetails from "./screens/Help/FirmDetails";
import Downloads from "./screens/Reports/Downloads";
import API from "./api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Prevent data from being cached
      cacheTime: 0,
      // Prevent queries from reusing stale data
      staleTime: 0,
      // Ensure data is always refetched
      refetchOnWindowFocus: false, // Default behavior; refetch on focus
      refetchOnReconnect: true, // Refetch on network reconnect
      refetchInterval: false, // Disable background refetching
    },
  },
});

const ProtectedRoutes = () => {
  const { isLoggedIn, userProfile } = getAuthData();
  if (isLoggedIn && userProfile?.kycStatus !== KYC_STATUS.APPROVED) {
    return <Navigate to="/kyc" />;
  }
  return isLoggedIn ? (
    <Outlet />
  ) : (
    <Navigate to={`/login?redirectUrl=${window.location.href}`} />
  );
};

const AuthRoutes = () => {
  const { isLoggedIn } = getAuthData();
  return isLoggedIn ? <Navigate to="/dashboard" /> : <Outlet />;
};

const KYCRoutes = () => {
  const { isLoggedIn, userProfile } = getAuthData();
  return isLoggedIn && userProfile?.kycStatus === KYC_STATUS.APPROVED ? (
    <Navigate to="/dashboard" />
  ) : (
    <Outlet />
  );
};

const HostBasedRoute = ({ appData }) => {
  // const isApiExchange = window.location.hostname.includes("apiexchange.co.in");

  return false ? <Home appData={appData} /> : <Navigate to="/login" replace />;
};

const App = () => {
  const [appData, setAppData] = useState({
    isLoading: false,
    appName: "",
    appLogo: "",
    appFavicon: "",
  });
  const { userProfile } = getAuthData();
  const theme = {
    colors: {
      authBackgroundColor: "#ffffff",
      primaryColor: "#0c4a54",
      secondaryColor: "#1a7c8c",
      tertiaryColor: "#42d8f5",
    },
  };

  const readMetaData = async () => {
    setAppData((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      API.get(`/meta`).then(({ data }) => {
        setAppData((prevState) => ({
          ...prevState,
          appName: data?.data?.appName,
          appLogo: data?.data?.logo,
          appFavicon: data?.data?.favicon,
          ...data?.data,
        }));
      });
    } catch (error) {
      console.error(error);
    } finally {
      setAppData((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    readMetaData();
  }, []);

  return (
    <>
      <MetaTags>
        <title>{appData.appName}</title>
        <meta name="description" content={appData.appName} />
        <meta property="og:title" content={appData.appName} />
        <meta property="og:image" content={appData.appLogo} />
        <link rel="icon" href={appData.appFavicon} />
        <link rel="apple-touch-icon" href={appData.appLogo} />
      </MetaTags>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <Router>
            <Routes>
              <Route path="/" element={<HostBasedRoute appData={appData} />} />
              <Route element={<ProtectedRoutes />}>
                <Route
                  exact
                  path="/dashboard"
                  element={<Dashboard appData={appData} />}
                />
                <Route
                  path={"/profile"}
                  element={<Profile appData={appData} />}
                />
                <Route
                  path={"/change-password"}
                  element={<ChangePassword appData={appData} />}
                />
                <Route path={"/logs"} element={<Logs appData={appData} />} />
                <Route
                  path={"/sessions"}
                  element={<Sessions appData={appData} />}
                />
                <Route
                  path={"/payments/wallet-topups"}
                  element={<WalletTopupsList appData={appData} />}
                />
                <Route
                  path={"/payments/wallet-topups/create"}
                  element={<WalletTopupsCreate appData={appData} />}
                />
                 {userProfile?.withdrawStatus === 'Active' && (
                  <>
                <Route
                  path="/payments/bank-accounts"
                  element={<BankAccountsList appData={appData} />}
                />
                <Route
                  path="/payments/bank-accounts/create"
                  element={<BankAccountsCreate appData={appData} />}
                />
                <Route
                  path="/payments/withdraw-funds"
                  element={<WithdrawFundsList appData={appData} />}
                />
                <Route
                  path="/payments/withdraw-funds/create"
                  element={<WithdrawFundsCreate appData={appData} />}
                /></> )}
                <Route
                  path={"/payments/switch-funds"}
                  element={<SwitchFundsList appData={appData} />}
                />
                <Route
                  path={"/payments/switch-funds/create"}
                  element={<SwitchFundsCreate appData={appData} />}
                />
                <Route
                  path={"/payments/fund-transfers"}
                  element={<FundTransfersList appData={appData} />}
                />
                {appData?.paymentGatewayStatus === 'Active' && (
                  <>
                    <Route
                      path={"/payments/payment-gateway-deposits"}
                      element={<PaymentGatewayDeposits appData={appData} />}
                    />{" "}
                    <Route
                      path={"/payments/payment-gateway-deposits-status/:id"}
                      element={
                        <PaymentGatewayDepositsStatus appData={appData} />
                      }
                    />
                  </>
                )}
                <Route
                  path={"/buyer/available-margins"}
                  element={<AvailableMarginsList appData={appData} />}
                />
                <Route
                  path={"/buyer/margins"}
                  element={<BuyerMarginsList appData={appData} />}
                />
                <Route
                  path={"/buyer/margins/create"}
                  element={<BuyerMarginsCreate appData={appData} />}
                />
                <Route
                  path={"/buyer/margins/update/:id"}
                  element={<BuyerMarginsUpdate appData={appData} />}
                />
                <Route
                  path={"/buyer/transactions"}
                  element={<BuyerTransactionsList appData={appData} />}
                />
                <Route
                  path={"/buyer/transactions-pending"}
                  element={<BuyerTransactionsPendingList appData={appData} />}
                />
                <Route
                  path={"/buyer/transactions-complaints"}
                  element={
                    <BuyerTransactionsComplaintsList appData={appData} />
                  }
                />
                <Route
                  path={"/buyer/search-transactions"}
                  element={<BuyerSearchTransactionsList appData={appData} />}
                />
                <Route
                  path={"/buyer/wrong-to-right-transactions"}
                  element={
                    <BuyerWrongToRightTransactionsList appData={appData} />
                  }
                />
                {/* <Route
                  path={"/buyer/api-credentials"}
                  element={<BuyerAPICredentials appData={appData} />}
                /> */}
                <Route
                  path={"/buyer/api-settings"}
                  element={<BuyerAPISettingsList appData={appData} />}
                />
                <Route
                  path={"/buyer/api-settings/create"}
                  element={<BuyerAPISettingsCreate appData={appData} />}
                />
                <Route
                  path={"/buyer/api-settings/update/:id"}
                  element={<BuyerAPISettingsUpdate appData={appData} />}
                />
                <Route
                  path={"/buyer/api-documentation"}
                  element={<BuyerAPIDocumentation appData={appData} />}
                />
                <Route
                  path={"/seller/api-settings"}
                  element={<SellerAPISettingsList appData={appData} />}
                />
                <Route
                  path={"/seller/api-settings/create"}
                  element={<SellerAPISettingsCreate appData={appData} />}
                />
                {(!userProfile?.sellersAPIEditStatus ||
                  userProfile?.sellersAPIEditStatus === "Yes" ||
                  userProfile?.canEditApis === "Active") && (
                    <Route
                      path={"/seller/api-settings/update/:id"}
                      element={<SellerAPISettingsUpdate appData={appData} />}
                    />
                  )}
                <Route
                  path={"/seller/margins"}
                  element={<SellerMarginsList appData={appData} />}
                />
                <Route
                  path={"/seller/margins/create"}
                  element={<SellerMarginsCreate appData={appData} />}
                />
                {(!userProfile?.sellersMarginEditStatus ||
                  userProfile?.sellersMarginEditStatus === "Yes") && (
                    <Route
                      path={"/seller/margins/update/:id"}
                      element={<SellerMarginsUpdate appData={appData} />}
                    />
                  )}
                <Route
                  path={"/seller/transactions"}
                  element={<SellerTransactionsList appData={appData} />}
                />
                <Route
                  path={"/seller/transactions-pending"}
                  element={<SellerTransactionsPendingList appData={appData} />}
                />
                <Route
                  path={"/seller/transactions-complaints"}
                  element={
                    <SellerTransactionsComplaintsList appData={appData} />
                  }
                />
                <Route
                  path={"/seller/search-transactions"}
                  element={<SellerSearchTransactionsList appData={appData} />}
                />
                <Route
                  path={"/seller/wrong-to-right-transactions"}
                  element={
                    <SellerWrongToRightTransactionsList appData={appData} />
                  }
                />
                <Route
                  path={"/seller/fake-complaints"}
                  element={<FakeComplaints appData={appData} />}
                />
                <Route
                  path={"/reports/operator-daybook"}
                  element={<OperatorDaybook appData={appData} />}
                />
                <Route
                  path={"/reports/daybook"}
                  element={<Daybook appData={appData} />}
                />
                <Route
                  path={"/reports/buyer-wallet-transactions"}
                  element={<BuyerWalletTransactions appData={appData} />}
                />
                <Route
                  path={"/reports/seller-wallet-transactions"}
                  element={<SellerWalletTransactions appData={appData} />}
                />
                <Route
                  path={"/reports/downloads"}
                  element={<Downloads appData={appData} />}
                />
                <Route
                  path={"/invoicing/buyer"}
                  element={<BuyerInvoicesList appData={appData} />}
                />
                <Route
                  path={"/invoicing/seller"}
                  element={<SellerInvoicesList appData={appData} />}
                />
                <Route
                  path={"/help/support"}
                  element={<Support appData={appData} />}
                />
                <Route
                  path={"/help/support/create"}
                  element={<SupportCreate appData={appData} />}
                />
                <Route
                  path={"/help/seller-api-support"}
                  element={<SellerAPISupport appData={appData} />}
                />
                <Route
                  path={"/help/seller-api-support/create"}
                  element={<SellerAPISupportCreate appData={appData} />}
                />
                <Route
                  path={"/help/company-profile"}
                  element={<FirmDetails appData={appData} />}
                />
              </Route>
              <Route element={<AuthRoutes appData={appData} />}>
                <Route
                  path={"/register"}
                  element={<Register appData={appData} />}
                />
                <Route path={"/login"} element={<Login appData={appData} />} />
                <Route
                  path={"/forgot-password"}
                  element={<ForgotPassword appData={appData} />}
                />
              </Route>
              <Route element={<KYCRoutes />}>
                <Route
                  path={"/kyc"}
                  element={<KYCPending appData={appData} />}
                />
              </Route>
              <Route
                path={"/privacy-policy"}
                element={<PrivacyPolicy appData={appData} />}
              />
              <Route
                path={"/disclaimers"}
                element={<Disclaimers appData={appData} />}
              />
              <Route
                path={"/terms-and-conditions"}
                element={<TermsAndConditions appData={appData} />}
              />
              <Route
                path={"/refund-policy"}
                element={<RefundPolicy appData={appData} />}
              />
              <Route
                path={"/contact"}
                element={<Contact appData={appData} />}
              />
              <Route path={"*"} element={<h1>Not Found</h1>} />
            </Routes>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
